import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import type { SmartGuideLine } from '../types/smart-guide-line'
import { edgesOf } from './edges-of'

export function segmentOverlap1d(a0: number, a1: number, b0: number, b1: number): number {
  return Math.max(0, Math.min(a1, b1) - Math.max(a0, b0))
}

export function minVerticalOverlapThree(a: ObjectBounds, b: ObjectBounds, c: ObjectBounds): number {
  const ab = segmentOverlap1d(a.y, a.y + a.height, b.y, b.y + b.height)
  const bc = segmentOverlap1d(b.y, b.y + b.height, c.y, c.y + c.height)

  return Math.min(ab, bc)
}

export function minHorizontalOverlapThree(
  a: ObjectBounds,
  b: ObjectBounds,
  c: ObjectBounds
): number {
  const ab = segmentOverlap1d(a.x, a.x + a.width, b.x, b.x + b.width)
  const bc = segmentOverlap1d(b.x, b.x + b.width, c.x, c.x + c.width)

  return Math.min(ab, bc)
}

export function minOverlapRequired(
  a: ObjectBounds,
  b: ObjectBounds,
  c: ObjectBounds,
  axis: 'x' | 'y'
): number {
  if (axis === 'y') {
    const minH = Math.min(a.height, b.height, c.height)

    return Math.min(8, Math.max(2, minH * 0.12))
  }

  const minW = Math.min(a.width, b.width, c.width)

  return Math.min(8, Math.max(2, minW * 0.12))
}

export const MAX_DISTRIBUTION_GUIDES = 4

/**
 * Equal-spacing guides when the moving object sits between immediate left/right (or top/bottom)
 * neighbors with matching gaps and sufficient overlap on the perpendicular axis.
 */
export function detectDistributionAlignment(
  movingBounds: ObjectBounds,
  otherBounds: ObjectBounds[],
  threshold: number
): SmartGuideLine[] {
  if (otherBounds.length < 2) return []

  const guides: SmartGuideLine[] = []
  const moving = edgesOf(movingBounds)

  let leftN: ObjectBounds | null = null
  let bestRight = -Infinity

  for (const o of otherBounds) {
    const r = o.x + o.width

    if (r < moving.left && r > bestRight) {
      bestRight = r
      leftN = o
    }
  }

  let rightN: ObjectBounds | null = null
  let bestLeft = Infinity

  for (const o of otherBounds) {
    if (o.x > moving.right && o.x < bestLeft) {
      bestLeft = o.x
      rightN = o
    }
  }

  if (leftN && rightN) {
    const lo = edgesOf(leftN)
    const ro = edgesOf(rightN)
    const gapL = moving.left - lo.right
    const gapR = ro.left - moving.right

    if (gapL > 0 && gapR > 0 && Math.abs(gapL - gapR) <= threshold) {
      const overlapY = minVerticalOverlapThree(leftN, movingBounds, rightN)
      const needY = minOverlapRequired(leftN, movingBounds, rightN, 'y')

      if (overlapY >= needY) {
        const minY = Math.min(lo.top, moving.top, ro.top)
        const maxY = Math.max(lo.bottom, moving.bottom, ro.bottom)
        const midY = (minY + maxY) / 2

        guides.push({
          type: 'horizontal',
          position: midY,
          from: lo.right,
          to: ro.left,
        })
      }
    }
  }

  let topN: ObjectBounds | null = null
  let bestBottom = -Infinity

  for (const o of otherBounds) {
    const bo = o.y + o.height

    if (bo < moving.top && bo > bestBottom) {
      bestBottom = bo
      topN = o
    }
  }

  let bottomN: ObjectBounds | null = null
  let bestTop = Infinity

  for (const o of otherBounds) {
    if (o.y > moving.bottom && o.y < bestTop) {
      bestTop = o.y
      bottomN = o
    }
  }

  if (topN && bottomN && guides.length < MAX_DISTRIBUTION_GUIDES) {
    const to = edgesOf(topN)
    const bo = edgesOf(bottomN)
    const gapT = moving.top - to.bottom
    const gapB = bo.top - moving.bottom

    if (gapT > 0 && gapB > 0 && Math.abs(gapT - gapB) <= threshold) {
      const overlapX = minHorizontalOverlapThree(topN, movingBounds, bottomN)
      const needX = minOverlapRequired(topN, movingBounds, bottomN, 'x')

      if (overlapX >= needX) {
        const minX = Math.min(to.left, moving.left, bo.left)
        const maxX = Math.max(to.right, moving.right, bo.right)
        const midX = (minX + maxX) / 2

        guides.push({
          type: 'vertical',
          position: midX,
          from: to.bottom,
          to: bo.top,
        })
      }
    }
  }

  return guides.slice(0, MAX_DISTRIBUTION_GUIDES)
}
